import { AppointmentProposalOptionSOW } from './appointmentProposalOptionSows.models';

export interface OptionSOWState {
  ids: string[];
  entities: Record<string, AppointmentProposalOptionSOW>;
  selectedProposalId: string | null;
  isLoaded: boolean;
  isLoading: boolean;
  isLoadingSOWIds: string[];
  loadedProposalIds: string[];
  error: string | null;
}

type Action =
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_SOWS';
      payload: { appointmentProposalId: string };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_SOWS_SUCCESS';
      payload: {
        optionSows: AppointmentProposalOptionSOW[];
        appointmentProposalId: string;
      };
    }
  | {
      type: 'FETCH_APPOINTMENT_PROPOSAL_SOWS_FAILURE';
      payload: { error: string };
    }
  | { type: 'CREATE_OPTION_SOW'; payload: { sowId: string } }
  | {
      type: 'CREATE_OPTION_SOW_SUCCESS';
      payload: { sow: AppointmentProposalOptionSOW };
    }
  | {
      type: 'CREATE_OPTION_SOW_FAILURE';
      payload: { error: string; sowId: string };
    }
  | {
      type: 'CREATE_MANY_OPTION_SOW';
      payload: { sowIds: string[] };
    }
  | {
      type: 'CREATE_MANY_OPTION_SOW_SUCCESS';
      payload: {
        sowIds: string[];
        appointmentProposalOptionSOWs: AppointmentProposalOptionSOW[];
      };
    }
  | {
      type: 'CREATE_MANY_OPTION_SOW_FAILURE';
      payload: { error: string; sowIds: string[] };
    }
  | { type: 'UPDATE_OPTION_SOW' }
  | {
      type: 'UPDATE_OPTION_SOW_SUCCESS';
      payload: { sow: AppointmentProposalOptionSOW };
    }
  | { type: 'UPDATE_OPTION_SOW_FAILURE'; payload: { error: string } }
  | { type: 'DELETE_OPTION_SOW' }
  | {
      type: 'DELETE_OPTION_SOW_SUCCESS';
      payload: { appointmentProposalOptionSOWId: string };
    }
  | { type: 'DELETE_OPTION_SOW_FAILURE'; payload: { error: string } }
  | {
      type: 'UPSERT_SOWS';
      payload: {
        appointmentProposalOptionSOWs: AppointmentProposalOptionSOW[];
      };
    }
  | {
      type: 'SET_SOWS_MUTATING';
      payload: { creatingIds?: string[] };
    }
  | {
      type: 'CLEAR_SOWS_MUTATING';
      payload: { creatingIds?: string[]; error?: string };
    }
  | {
      type: 'SELECT_PROPOSAL_SOWS';
      payload: { appointmentProposalId: string };
    };

export const initState: OptionSOWState = {
  ids: [],
  entities: {},
  selectedProposalId: null,
  isLoaded: false,
  isLoading: true,
  isLoadingSOWIds: [],
  loadedProposalIds: [],
  error: null,
};

export const optionSOWReducer = (
  state: OptionSOWState,
  action: Action
): OptionSOWState => {
  switch (action.type) {
    case 'FETCH_APPOINTMENT_PROPOSAL_SOWS':
      return {
        ...state,
        error: null,
        isLoading: true,
        isLoaded: state.loadedProposalIds.includes(
          action.payload.appointmentProposalId
        ),
        selectedProposalId: action.payload.appointmentProposalId,
      };
    case 'FETCH_APPOINTMENT_PROPOSAL_SOWS_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        loadedProposalIds: [
          ...new Set([
            ...state.loadedProposalIds,
            action.payload.appointmentProposalId,
          ]),
        ],
        ids: [
          ...new Set([
            ...state.ids,
            ...action.payload.optionSows.map(
              (s) => s.appointmentProposalOptionSOWId
            ),
          ]),
        ],
        entities: action.payload.optionSows.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.appointmentProposalOptionSOWId]: curr,
          }),
          state.entities
        ),
      };

    case 'CREATE_MANY_OPTION_SOW':
      return {
        ...state,
        isLoadingSOWIds: [
          ...new Set([...state.isLoadingSOWIds, ...action.payload.sowIds]),
        ],
        error: null,
      };

    case 'CREATE_MANY_OPTION_SOW_SUCCESS':
      return {
        ...state,
        isLoadingSOWIds: state.isLoadingSOWIds.filter(
          (id) => !action.payload.sowIds.includes(id)
        ),
        ids: [
          ...new Set([
            ...state.ids,
            ...action.payload.appointmentProposalOptionSOWs.map(
              (p) => p.appointmentProposalOptionSOWId
            ),
          ]),
        ],
        entities: action.payload.appointmentProposalOptionSOWs.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.appointmentProposalOptionSOWId]: curr,
          }),
          state.entities
        ),
        error: null,
      };

    case 'CREATE_MANY_OPTION_SOW_FAILURE':
      return {
        ...state,
        isLoadingSOWIds: state.isLoadingSOWIds.filter(
          (id) => !action.payload.sowIds.includes(id)
        ),
        error: action.payload.error,
      };

    case 'CREATE_OPTION_SOW':
      return {
        ...state,
        isLoadingSOWIds: [
          ...new Set([...state.isLoadingSOWIds, action.payload.sowId]),
        ],
        error: null,
      };
    case 'UPDATE_OPTION_SOW':
    case 'DELETE_OPTION_SOW':
      return { ...state, isLoading: true, error: null };
    case 'CREATE_OPTION_SOW_SUCCESS':
    case 'UPDATE_OPTION_SOW_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoadingSOWIds: state.isLoadingSOWIds.filter(
          (id) => id !== action.payload.sow.sowId
        ),
        ids: [
          ...new Set([
            ...state.ids,
            action.payload.sow.appointmentProposalOptionSOWId,
          ]),
        ],
        entities: {
          ...state.entities,
          [action.payload.sow.appointmentProposalOptionSOWId]:
            action.payload.sow,
        },
      };
    case 'DELETE_OPTION_SOW_SUCCESS':
      return {
        ...state,
        isLoading: false,
        ids: state.ids.filter(
          (id) => id !== action.payload.appointmentProposalOptionSOWId
        ),
      };
    case 'FETCH_APPOINTMENT_PROPOSAL_SOWS_FAILURE':
    case 'UPDATE_OPTION_SOW_FAILURE':
    case 'DELETE_OPTION_SOW_FAILURE':
      return { ...state, isLoading: false, error: action.payload.error };
    case 'CREATE_OPTION_SOW_FAILURE':
      return {
        ...state,
        isLoading: false,
        isLoadingSOWIds: state.isLoadingSOWIds.filter(
          (id) => id !== action.payload.sowId
        ),
        error: action.payload.error,
      };

    case 'SELECT_PROPOSAL_SOWS': {
      const isLoaded = state.loadedProposalIds.includes(
        action.payload.appointmentProposalId
      );
      return {
        ...state,
        error: null,
        isLoading: !isLoaded,
        isLoaded: isLoaded,
        selectedProposalId: action.payload.appointmentProposalId,
      };
    }

    case 'UPSERT_SOWS': {
      const sows = action.payload.appointmentProposalOptionSOWs;
      const arrivedSowIds = sows.map((s) => s.sowId);
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        isLoadingSOWIds: state.isLoadingSOWIds.filter(
          (id) => !arrivedSowIds.includes(id)
        ),
        loadedProposalIds: [
          ...new Set([
            ...state.loadedProposalIds,
            ...sows.map((s) => s.appointmentProposalId),
          ]),
        ],
        ids: [
          ...new Set([
            ...state.ids,
            ...sows.map((s) => s.appointmentProposalOptionSOWId),
          ]),
        ],
        entities: sows.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.appointmentProposalOptionSOWId]: curr,
          }),
          state.entities
        ),
      };
    }

    case 'SET_SOWS_MUTATING':
      return {
        ...state,
        error: null,
        isLoadingSOWIds: action.payload.creatingIds
          ? [
              ...new Set([
                ...state.isLoadingSOWIds,
                ...action.payload.creatingIds,
              ]),
            ]
          : state.isLoadingSOWIds,
        isLoading: action.payload.creatingIds ? state.isLoading : true,
      };

    case 'CLEAR_SOWS_MUTATING':
      return {
        ...state,
        error: action.payload.error ?? state.error,
        isLoadingSOWIds: action.payload.creatingIds
          ? state.isLoadingSOWIds.filter(
              (id) => !action.payload.creatingIds?.includes(id)
            )
          : state.isLoadingSOWIds,
        isLoading: action.payload.creatingIds ? state.isLoading : false,
      };

    default:
      return state;
  }
};
